import supabase from '../config/supabaseClient';
import { calculateNextPayment } from '../utils/dateUtils';

export const subscriptionService = {
  async getAll() {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async create(subscription) {
    const { data, error } = await supabase
      .from('subscriptions')
      .insert([subscription])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async update(id, subscription) {
    const { data, error } = await supabase
      .from('subscriptions')
      .update(subscription)
      .eq('id', id)
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async delete(id) {
    const { error } = await supabase
      .from('subscriptions')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async getSubscriptionCount(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const { count, error } = await supabase
        .from('subscriptions')
        .select('*', { count: 'exact' })
        .eq('user_id', userId);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error getting subscription count:', error);
      throw error;
    }
  },

  async getAllSubscriptions(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      console.log('Fetching subscriptions for user:', userId); // Debug log

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching subscriptions:', error);
        throw error;
      }

      console.log('Fetched subscriptions:', data); // Debug log
      return data || [];
    } catch (error) {
      console.error('Error getting subscriptions:', error);
      throw error;
    }
  },

  async getSubscriptionsByCycle(userId, cycle) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('cycle', cycle)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting subscriptions by cycle:', error);
      throw error;
    }
  },

  async createSubscription(subscriptionData) {
    try {
      console.log('Creating subscription with data:', subscriptionData);

      const formattedData = {
        user_id: subscriptionData.user_id,
        name: subscriptionData.name,
        provider: subscriptionData.provider,
        price: parseFloat(subscriptionData.price),
        cycle: subscriptionData.cycle,
        payment_method: subscriptionData.payment_method,
        start_date: new Date(subscriptionData.start_date).toISOString(),
        created_at: new Date().toISOString(),
        tags: Array.isArray(subscriptionData.tags) ? subscriptionData.tags : [],
        company_name: subscriptionData.name,
        company_category: subscriptionData.category || '',
        company_logo: subscriptionData.logo || ''
      };

      console.log('Formatted data:', formattedData);

      const { data, error } = await supabase
        .from('subscriptions')
        .insert([formattedData])
        .select('*');

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Created subscription:', data);
      return data[0];
    } catch (error) {
      console.error('Error in createSubscription:', error);
      throw error;
    }
  },

  async getUserSubscription(userId) {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user subscription:', error);
      throw error;
    }
  },

  async updateUserSubscription(userId, subscriptionData) {
    try {
      // First try to get existing subscription
      const { data: existing } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (existing) {
        // Update existing subscription
        const { data, error } = await supabase
          .from('user_subscriptions')
          .update(subscriptionData)
          .eq('user_id', userId)
          .select();

        if (error) throw error;
        return data[0];
      } else {
        // Insert new subscription
        const { data, error } = await supabase
          .from('user_subscriptions')
          .insert([subscriptionData])
          .select();

        if (error) throw error;
        return data[0];
      }
    } catch (error) {
      console.error('Error updating user subscription:', error);
      throw error;
    }
  },

  async getDashboardStats(userId) {
    try {
      const [totalCount, subscriptions] = await Promise.all([
        this.getSubscriptionCount(userId),
        this.getAllSubscriptions(userId)
      ]);

      const activeCount = subscriptions.filter(sub => sub.status === 'active').length;

      return {
        totalSubscriptions: totalCount,
        activeSubscriptions: activeCount
      };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      throw error;
    }
  },

  async deleteSubscription(id) {
    try {
      if (!id) {
        throw new Error('Subscription ID is required');
      }

      const { error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error deleting subscription:', error);
      throw error;
    }
  },

  async updateSubscription(id, subscriptionData) {
    try {
      if (!id) throw new Error('Subscription ID is required');
      
      console.log('Updating subscription:', id, subscriptionData);

      // Format the data for update
      const formattedData = {
        user_id: subscriptionData.user_id,
        name: subscriptionData.name,
        provider: subscriptionData.provider,
        price: parseFloat(subscriptionData.price),
        cycle: subscriptionData.cycle,
        payment_method: subscriptionData.payment_method,
        start_date: new Date(subscriptionData.start_date).toISOString(),
        tags: Array.isArray(subscriptionData.tags) ? subscriptionData.tags : 
              subscriptionData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        updated_at: new Date().toISOString(),
        status: 'active',
        company_name: subscriptionData.name,
        company_category: subscriptionData.category || '',
        company_logo: subscriptionData.logo || ''
      };

      console.log('Formatted update data:', formattedData);

      // First verify the subscription exists and belongs to the user
      const { data: existingData, error: fetchError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        console.error('Error fetching subscription:', fetchError);
        throw fetchError;
      }

      if (!existingData) {
        throw new Error('Subscription not found');
      }

      // Perform the update
      const { data, error } = await supabase
        .from('subscriptions')
        .update(formattedData)
        .eq('id', id)
        .eq('user_id', subscriptionData.user_id)
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Failed to update subscription');
      }

      console.log('Successfully updated subscription:', data);
      return data;
    } catch (error) {
      console.error('Error in updateSubscription:', error);
      throw error;
    }
  },

  async getSubscriptionById(id) {
    try {
      if (!id) throw new Error('Subscription ID is required');

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Subscription not found');
      }

      console.log('Fetched subscription:', data);
      return data;
    } catch (error) {
      console.error('Error getting subscription by ID:', error);
      throw error;
    }
  }
}; 